import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { Anomaly } from '../types';
import FilterBar from '../components/FilterBar';
import SeverityBadge from '../components/SeverityBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

const AnomaliesList: React.FC = () => {
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  const fetchAnomalies = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    const qs = params.toString();
    api
      .get<Anomaly[]>(`/anomalies${qs ? `?${qs}` : ''}`)
      .then(setAnomalies)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    fetchAnomalies();
  }, [fetchAnomalies]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h1>Anomalies</h1>
          <p className="text-sm text-slate-500 mt-1">
            {anomalies.length} match{anomalies.length === 1 ? '' : 'es'}
          </p>
        </div>
      </div>
      <FilterBar filters={filters} onFilterChange={handleFilterChange} />

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : (
        <div className="card overflow-x-auto">
          <table className="table-base">
            <thead>
              <tr>
                <th>App</th>
                <th>Stack</th>
                <th>Account</th>
                <th>Region</th>
                <th>Priority</th>
                <th>Log Group</th>
                <th>Reported</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {anomalies.map((a) => (
                <tr
                  key={a.compositeKey}
                  onClick={() => navigate(`/anomalies/${a.accountId}/${a.region}/${a.anomalyId}`)}
                  className="cursor-pointer"
                >
                  <td className="font-medium text-slate-900">{a.appId}</td>
                  <td>{a.stackId}</td>
                  <td className="font-mono text-xs text-slate-600">{a.accountId}</td>
                  <td className="text-slate-600">{a.region}</td>
                  <td><SeverityBadge priority={a.priority} /></td>
                  <td className="font-mono text-xs text-slate-600 truncate max-w-xs">{a.logGroupName}</td>
                  <td className="text-slate-600 whitespace-nowrap">{new Date(a.reportedAt).toLocaleString()}</td>
                  <td>
                    {a.ackState ? (
                      <span className="badge-ok">Acked</span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {anomalies.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-slate-500 py-8">
                    No anomalies match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AnomaliesList;
